import React, { useState, useEffect } from 'react';
import { getToken } from '../../services/auth';
import { API_PATHS } from '../../utils/constants';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachmentIcon from '@mui/icons-material/Attachment';

/**
 * A reusable document viewer component that displays documents in a modal dialog
 * 
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the dialog is open
 * @param {function} props.onClose Function to call when dialog is closed
 * @param {string} props.documentUrl URL or identifier of the document to display
 * @param {string} props.fileName Optional filename to display
 * @param {string} props.uid Optional user ID for workflow documents
 * @param {string} props.documentType Optional document type for workflow documents
 */
const DocumentViewer = ({ open, onClose, documentUrl, fileName, uid, documentType }) => {
  const [viewerState, setViewerState] = useState({
    fileName: fileName || 'Document',
    fileUrl: '',
    fileType: '',
    loading: false,
    error: null
  });

  useEffect(() => {
    console.log('==== DOCUMENT VIEWER COMPONENT DEBUG ====');
    console.log('DocumentViewer useEffect triggered with:', { 
      open, 
      documentUrl, 
      uid, 
      documentType,
      fileName
    });
    
    if (open && documentUrl) {
      console.log('Loading document...');
      loadDocument();
    }
    
    // Cleanup function to revoke object URL when component unmounts or dialog closes
    return () => {
      if (viewerState.fileUrl && viewerState.fileUrl.startsWith('blob:')) {
        console.log('Cleaning up blob URL:', viewerState.fileUrl);
        URL.revokeObjectURL(viewerState.fileUrl);
      }
    };
  }, [open, documentUrl, uid, documentType, fileName]);

  const loadDocument = async () => {
    console.log('loadDocument called with URL:', documentUrl);
    console.log('Document type:', documentType);
    console.log('UID:', uid);
    console.log('File name:', fileName);
    setViewerState(prev => ({ ...prev, loading: true, error: null }));
    
    if (!documentUrl) {
      console.error('Document URL is missing');
      setViewerState(prev => ({
        ...prev,
        loading: false,
        error: 'Document URL is missing'
      }));
      return;
    }

    console.log('Setting loading state to true');
    setViewerState(prev => ({
      ...prev,
      loading: true,
      error: null,
      fileUrl: '',
      fileType: ''
    }));

    const token = getToken();
    console.log('Auth token retrieved:', token ? 'Token found' : 'No token');
    
    if (!token) {
      console.error('Authentication token not found');
      setViewerState(prev => ({
        ...prev,
        loading: false,
        error: 'Authentication token not found. Please log in again.'
      }));
      return;
    }

    try {
      // Determine the correct download URL based on the input
      let downloadUrl = '';
      
      // Try to parse document data if it's a JSON string
      let parsedDoc = null;
      try {
        if (typeof documentUrl === 'string' && documentUrl.startsWith('{') && documentUrl.endsWith('}')) {
          parsedDoc = JSON.parse(documentUrl);
          console.log('Parsed document data:', parsedDoc);
        }
      } catch (e) {
        console.log('Not a valid JSON string, continuing with normal URL processing');
      }
      
      // If we have parsed document data with uid and documentType
      if (parsedDoc && parsedDoc.uid && parsedDoc.documentType) {
        // Check if this is a dynamic service field (UUID pattern)
        const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(parsedDoc.documentType);
        
        if (isDynamicServiceField) {
          // For dynamic service fields, use original URL
          console.log('Dynamic service field detected in parsed doc, using original URL:', documentUrl);
          downloadUrl = documentUrl.startsWith('/') ? 
            `${API_PATHS.BASE}${documentUrl}` : 
            documentUrl;
        } else {
          downloadUrl = API_PATHS.DOCUMENT_DOWNLOAD(parsedDoc.uid, parsedDoc.documentType);
          console.log('Using parsed document data for workflow document:', downloadUrl);
        }
      }
      // If uid and documentType are provided as props
      else if (uid && documentType) {
        // Check if this is a dynamic service field (UUID pattern)
        const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(documentType);
        
        if (isDynamicServiceField) {
          // For dynamic service fields, use original URL
          console.log('Dynamic service field detected in props, using original URL:', documentUrl);
          downloadUrl = documentUrl.startsWith('/') ? 
            `${API_PATHS.BASE}${documentUrl}` : 
            documentUrl;
        } else {
          downloadUrl = API_PATHS.DOCUMENT_DOWNLOAD(uid, documentType);
          console.log('Using props for workflow document:', downloadUrl);
        }
      }
      // Case 1: URL already contains /api/files/download or /v1/files/download endpoint
      else if (documentUrl.includes('/api/files/download') || documentUrl.includes('/v1/files/download')) {
        // This is already a file download URL, just ensure it has the full base URL
        if (documentUrl.startsWith('/')) {
          downloadUrl = `${API_PATHS.BASE}${documentUrl}`;
        } else if (!documentUrl.startsWith('http')) {
          downloadUrl = `${API_PATHS.BASE}/${documentUrl}`;
        } else {
          downloadUrl = documentUrl;
        }
        console.log('Using existing file download URL:', downloadUrl);
      }
      // Case 2: Document reference URL pattern /v1/users/{uid}/documents/{documentType}/download
      else if (documentUrl.includes('/users/') && documentUrl.includes('/documents/')) {
        // This is a workflow ticket document - use the document download endpoint
        const docRefMatch = documentUrl.match(/\/(?:v1|api)\/users\/([^\/]+)\/documents\/([^\/]+)\/download/);
        
        if (docRefMatch) {
          const docUid = docRefMatch[1];
          const docType = docRefMatch[2];
          
          // Check if this is a dynamic service field (UUID pattern)
          const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(docType);
          
          if (isDynamicServiceField) {
            // For dynamic service fields, this URL should not be used - fallback to original URL
            console.log('Dynamic service field detected in DocumentViewer, using original URL:', documentUrl);
            downloadUrl = documentUrl.startsWith('/') ? 
              `${API_PATHS.BASE}${documentUrl}` : 
              documentUrl;
          } else {
            // For static document fields, use employee document API
            downloadUrl = API_PATHS.DOCUMENT_DOWNLOAD(docUid, docType);
            console.log('Using document reference URL (workflow ticket):', downloadUrl);
          }
        } else {
          // Try to extract UID and document type from the URL
          const parts = documentUrl.split('/');
          if (parts.length >= 6) {
            const docUid = parts[3];
            const docType = parts[5];
            
            // Check if this is a dynamic service field (UUID pattern)
            const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(docType);
            
            if (isDynamicServiceField) {
              // For dynamic service fields, use original URL
              console.log('Dynamic service field detected in fallback extraction, using original URL:', documentUrl);
              downloadUrl = documentUrl.startsWith('/') ? 
                `${API_PATHS.BASE}${documentUrl}` : 
                documentUrl;
            } else {
              // For static document fields, use employee document API
              downloadUrl = API_PATHS.DOCUMENT_DOWNLOAD(docUid, docType);
              console.log('Using extracted document reference URL (workflow ticket):', downloadUrl);
            }
          } else {
            // Fallback to using the full URL
            downloadUrl = documentUrl.startsWith('/') ? 
              `${API_PATHS.BASE}${documentUrl}` : 
              documentUrl;
            console.log('Using full URL:', downloadUrl);
          }
        }
      }
      // Case 3: Check if it's a file path (contains / or file extension) vs document ID
      else {
        // Check if it looks like a file path (contains / or has file extension)
        const isFilePath = documentUrl.includes('/') || /\.[a-zA-Z0-9]+$/.test(documentUrl);
        
        if (isFilePath) {
          // It's a file path - use the file download endpoint
          const encodedPath = encodeURIComponent(documentUrl);
          downloadUrl = `${API_PATHS.BASE}/v1/files/download?filePath=${encodedPath}`;
          console.log('Using file download URL for file path:', downloadUrl);
        } else {
          // It's a document ID - use the document download endpoint
          downloadUrl = `${API_PATHS.BASE}/v1/users/documents/download?documentId=${documentUrl}`;
          console.log('Using document ID download URL for document ID:', downloadUrl);
        }
      }
      
      console.log('Final download URL:', downloadUrl);
      
      // Fetch the document
      let response;
      try {
        console.log('Fetching document with auth token...');
        // Fetch the document
        response = await fetch(downloadUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('Fetch response status:', response.status, response.statusText);
        console.log('Response headers:', [...response.headers.entries()]);
        
        if (!response.ok) {
          console.error(`HTTP error ${response.status}: ${response.statusText}`);
          throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
        }
        
        console.log('Document fetch successful');
      } catch (fetchError) {
        console.error('Fetch operation failed:', fetchError);
        throw fetchError;
      }
      
      try {
        console.log('Processing document response...');
        const blob = await response.blob();
        console.log('Blob received:', blob.type, blob.size, 'bytes');
        
        const objectUrl = URL.createObjectURL(blob);
        console.log('Object URL created:', objectUrl);
        
        const contentType = blob.type;
        
        // Extract filename from Content-Disposition header if available
        const contentDisposition = response.headers.get('Content-Disposition');
        console.log('Content-Disposition header:', contentDisposition);
        
        let extractedFileName = fileName || 'Document';
      
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
          if (filenameMatch && filenameMatch[1]) {
            extractedFileName = filenameMatch[1];
          }
        }
      
        console.log('Document loaded successfully, content type:', contentType);
      
        setViewerState({
          fileName: extractedFileName,
          fileUrl: objectUrl,
          fileType: contentType,
          loading: false,
          error: null
        });
      } catch (processingError) {
        console.error('Error processing document response:', processingError);
        throw processingError;
      }
      
    } catch (error) {
      console.error('Error loading document:', error);
      setViewerState(prev => ({
        ...prev,
        loading: false,
        error: `Failed to load document: ${error.message}`
      }));
    }
  };

  const handleClose = () => {
    // Clean up the object URL to prevent memory leaks
    if (viewerState.fileUrl && viewerState.fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(viewerState.fileUrl);
    }
    
    // Reset state
    setViewerState(prev => ({
      ...prev,
      fileUrl: '',
      fileType: '',
      loading: false,
      error: null
    }));
    
    // Call the parent's onClose handler
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
          width: '95vw',
          maxWidth: '95vw',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="h6" component="div">
          {viewerState.fileName || 'Document Viewer'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CancelIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 0 }}>
        {viewerState.loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading document...
            </Typography>
          </Box>
        ) : viewerState.error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="error" gutterBottom>
              Error Loading Document
            </Typography>
            <Typography variant="body1" paragraph>
              {viewerState.error}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please try again or contact support if the problem persists.
            </Typography>
          </Box>
        ) : viewerState.fileType && viewerState.fileUrl ? (
          viewerState.fileType.includes('image') ? (
            <img 
              src={viewerState.fileUrl} 
              alt={viewerState.fileName} 
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
            />
          ) : viewerState.fileType.includes('pdf') ? (
            <object 
              data={viewerState.fileUrl} 
              type="application/pdf" 
              width="100%" 
              height="100%"
              style={{ border: 'none' }}
            >
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" paragraph>
                  Unable to display PDF.
                </Typography>
                <Button 
                  variant="contained" 
                  href={viewerState.fileUrl} 
                  download={viewerState.fileName}
                >
                  Download Instead
                </Button>
              </Box>
            </object>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" paragraph>
                This file type cannot be previewed directly.
              </Typography>
              <Button 
                variant="contained" 
                href={viewerState.fileUrl} 
                download={viewerState.fileName}
              >
                Download File
              </Button>
            </Box>
          )
        ) : (
          <Typography variant="body1" color="textSecondary">
            No document to display
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
        >
          Close
        </Button>
        {viewerState.fileUrl && !viewerState.loading && !viewerState.error && (
          <Button 
            variant="contained" 
            href={viewerState.fileUrl} 
            download={viewerState.fileName}
            startIcon={<AttachmentIcon />}
          >
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewer;
